import type { FC } from 'react'
import { useState } from 'react'

import { Popup, Input } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { getQueryFilter } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import PlusIcon from 'mdi-react/PlusIcon'
import { useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useSearch } from '@/hooks/searchParams'
import type { LogSmsPush } from '@/modules/admin/resources'

import queries from '../queries'
import adminResources from '../resources'

import { ComposeSMSModal } from './ComposeSMSModal'
import { SMSLogsList } from './SMSLogList'
import { SMSLogReceiversListModal } from './SMSLogReceiversListModal'

export const SMSListPage: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslation()

  const [composeSMSModal, setComposeSMSModal] = useState(false)
  const [selectedBatch, setSelectedBatch] = useState<LogSmsPush | null>(null)

  const { searchFilter, setSearchFilter } = useSearch()

  const { data } = useQuery(queries.adminSMSLogsList(), adminResources.getSMSLogsList)

  const stringMatcher = (item: LogSmsPush) => [
    item.sender,
    item.message,
    item.receiver,
    item.isPrivate ? 'Private' : 'Public',
  ]

  const logsFiltered =
    data?.filter(sms => {
      const isQueryFilterMet = getQueryFilter(searchFilter, stringMatcher)(sms)
      return isQueryFilterMet
    }) || []

  return (
    <Container onSwipedLeft={() => navigate('/')}>
      <Container.Header>
        <Translation id="SMS Sender" />
      </Container.Header>

      <Button Icon={<PlusIcon />} className="mb-4 ml-0" onClick={() => setComposeSMSModal(true)}>
        <Translation id="New SMS" />
      </Button>

      <div className="table-actions">
        <Input
          id="department-search"
          value={searchFilter}
          placeholder={translate('Search')}
          onChange={e => setSearchFilter(e.target.value)}
        />
      </div>

      <SMSLogsList
        onClick={log => setSelectedBatch(log)}
        searchFilter={searchFilter}
        logs={logsFiltered}
      />

      <Popup
        variant="wide"
        isOpen={composeSMSModal}
        ariaLabel={translate('New SMS')}
        onClose={() => setComposeSMSModal(false)}
      >
        <ComposeSMSModal onClose={() => setComposeSMSModal(false)} />
      </Popup>

      <Popup
        variant="medium"
        isOpen={!!selectedBatch}
        ariaLabel="Test"
        onClose={() => setSelectedBatch(null)}
      >
        {!!selectedBatch && <SMSLogReceiversListModal log={selectedBatch} />}
      </Popup>
    </Container>
  )
}
